import React, { createContext, useContext, useMemo } from "react";
import { type ThemeTokens, defaultThemeTokens, type DeepPartial } from "./themeTokens";
import { mapThemeToDesignTokens } from "./themeMapping";
import type { DesignTokens } from "./tokens";
import { createThemeWithValidation } from './themeUtils';

export type ThemeContextValue = {
  theme: ThemeTokens;
  tokens: ThemeTokens;      // semantic (recommended new usage)
  designTokens: DesignTokens; // legacy compatibility
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  theme?: DeepPartial<ThemeTokens>;
  children: React.ReactNode;
}

export function ThemeProvider({
  theme,
  children,
}: ThemeProviderProps) {
  const merged: ThemeTokens = useMemo(() => {
    return createThemeWithValidation(theme);
  }, [theme]);

  const designTokens = useMemo(() => mapThemeToDesignTokens(merged), [merged]);

  const ctx: ThemeContextValue = useMemo(
    () => ({ theme: merged, tokens: merged, designTokens }),
    [merged, designTokens]
  );

  return <ThemeContext.Provider value={ctx}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

